<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import DashboardSidebar from '@/components/dashboard/shell/DashboardSidebar.vue'
import DashboardTopbar from '@/components/dashboard/shell/DashboardTopbar.vue'
import DashboardDataLoading from '@/components/dashboard/ui/DashboardDataLoading.vue'
import {
  consumeNavFeedback,
  useDashboardNavFeedback,
} from '@/composables/useDashboardNavFeedback'
import {
  registerDashboardMobileNav,
  unregisterDashboardMobileNav,
} from '@/composables/useDashboardMobileNav'
import { scrollWindowToTop } from '@/helpers/scrollWindowToTop'

const route = useRoute()
const mobileNavOpen = ref(false)
const { pendingRouteName } = useDashboardNavFeedback()

const isNavPending = computed(() => pendingRouteName.value !== null)

const toggleMobileNav = () => {
  mobileNavOpen.value = !mobileNavOpen.value
}

const closeMobileNav = () => {
  mobileNavOpen.value = false
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && mobileNavOpen.value) {
    closeMobileNav()
  }
}

watch(
  () => route.fullPath,
  () => {
    closeMobileNav()
    consumeNavFeedback()
    // In-dashboard navigations should always open at the top of the page.
    scrollWindowToTop()
  }
)

watch(mobileNavOpen, (open) => {
  document.body.classList.toggle('sa-nav-open', open)
})

onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown)
  registerDashboardMobileNav({
    open: () => {
      mobileNavOpen.value = true
    },
    close: closeMobileNav,
  })
})

onUnmounted(() => {
  document.body.classList.remove('sa-nav-open')
  document.removeEventListener('keydown', onDocumentKeydown)
  unregisterDashboardMobileNav()
})
</script>

<template>
  <div class="sa-shell">
    <DashboardSidebar :mobile-open="mobileNavOpen" />
    <div
      class="sa-nav-backdrop"
      :class="{ 'sa-nav-backdrop--show': mobileNavOpen }"
      aria-hidden="true"
      @click="closeMobileNav"
    />
    <div class="sa-main">
      <DashboardTopbar
        :mobile-nav-open="mobileNavOpen"
        @toggle-mobile-nav="toggleMobileNav"
      />
      <div class="sa-content">
        <DashboardDataLoading v-if="isNavPending" />
        <div v-show="!isNavPending">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
